/**
 * @file CookieConsent.js — Bannière de consentement aux cookies (R-0126 RGPD).
 * Affiche un bandeau discret en bas de l'écran si l'utilisateur n'a pas encore
 * acquitté le consentement (clé localStorage `ringo-cookie-consent`).
 * Le bouton « Accept » enregistre le consentement et masque la bannière.
 * Le bouton « Learn more » redirige vers la page /cookies.
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { APP_ROUTES } from '../lib/routes';

/** Clé localStorage utilisée pour mémoriser le consentement cookies. */
const CONSENT_KEY = 'ringo-cookie-consent';

/**
 * Composant de bannière de consentement aux cookies.
 * Vérifie localStorage au montage ; ne s'affiche que si le consentement
 * n'a pas encore été donné. Positionnement fixe en bas de page,
 * design minimaliste et non intrusif.
 * @returns {React.ReactElement|null} La bannière ou null si le consentement existe déjà.
 */
export default function CookieConsent() {
  /** @type {[boolean, Function]} Indique si la bannière doit être visible. */
  const [visible, setVisible] = useState(false);

  /**
   * Effet exécuté au montage : vérifie si le consentement a déjà été donné.
   * Si la clé n'existe pas dans localStorage, la bannière devient visible.
   */
  useEffect(() => {
    const consent = localStorage.getItem(CONSENT_KEY);
    if (!consent) {
      setVisible(true);
    }
  }, []);

  /**
   * Gestionnaire du clic sur « Accept ».
   * Enregistre la date du consentement dans localStorage et masque la bannière.
   */
  const handleAccept = () => {
    localStorage.setItem(CONSENT_KEY, new Date().toISOString());
    setVisible(false);
  };

  /* Ne rien afficher si le consentement a déjà été donné */
  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-background/95 backdrop-blur-sm"
      data-testid="cookie-consent"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-col sm:flex-row items-center justify-between gap-3">
        {/* ── Texte informatif ── */}
        <p
          className="text-xs text-muted-foreground text-center sm:text-left leading-relaxed"
          style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
        >
          This site uses essential cookies for authentication and security.
          No tracking or analytics cookies are used.
        </p>

        {/* ── Boutons d'action ── */}
        <div className="flex items-center gap-2 shrink-0">
          <Button
            asChild
            variant="ghost"
            size="sm"
            className="text-xs text-muted-foreground hover:text-foreground"
            data-testid="cookie-consent-learn-more"
          >
            <Link to={APP_ROUTES.cookies}>Learn more</Link>
          </Button>
          <Button
            size="sm"
            className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 text-xs"
            onClick={handleAccept}
            data-testid="cookie-consent-accept"
          >
            Accept
          </Button>
        </div>
      </div>
    </div>
  );
}
